import React, { useState, useEffect } from 'react';

interface TimeInput12HourProps {
  value: string; // ISO date string or datetime-local format
  onChange: (value: string) => void;
  className?: string;
  label?: string;
  id?: string;
}

const TimeInput12Hour: React.FC<TimeInput12HourProps> = ({ 
  value, 
  onChange, 
  className = '', 
  label,
  id 
}) => {
  // State for individual components
  const [date, setDate] = useState('');
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('PM');

  // Parse incoming value and update state
  useEffect(() => {
    if (!value) {
      // Default to today at 4 PM for new values
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      setDate(todayStr);
      setHour('04');
      setMinute('00');
      setPeriod('PM');
      return;
    }
    
    try {
      // Parse the ISO string as UK timezone date
      const dateObj = new Date(value);
      
      // Extract date components directly from the Date object to avoid timezone shifts
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Extract time components directly from the Date object (treating as UK time)
      const hour24 = dateObj.getHours();
      const minute24 = dateObj.getMinutes();
      
      // Convert to 12-hour format
      const isPM = hour24 >= 12;
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      
      setDate(dateStr);
      setHour(String(hour12).padStart(2, '0'));
      setMinute(String(minute24).padStart(2, '0'));
      setPeriod(isPM ? 'PM' : 'AM');
    } catch (error) {
      console.error('Error parsing date:', error);
      // Fallback to defaults
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      setDate(todayStr);
      setHour('04');
      setMinute('00');
      setPeriod('PM');
    }
  }, [value]);

  // Build and send new datetime when any component changes
  const updateDateTime = (newDate: string, newHour: string, newMinute: string, newPeriod: string) => {
    try {
      // Convert 12-hour to 24-hour
      let hour24 = parseInt(newHour);
      if (newPeriod === 'AM' && hour24 === 12) {
        hour24 = 0;
      } else if (newPeriod === 'PM' && hour24 !== 12) {
        hour24 += 12;
      }
      
      // Create datetime string explicitly in UK timezone to avoid local timezone shifts
      const timeStr = `${String(hour24).padStart(2, '0')}:${newMinute}:00`;
      
      // Create date in UK timezone using UTC methods to prevent local timezone conversion
      const year = parseInt(newDate.split('-')[0]);
      const month = parseInt(newDate.split('-')[1]) - 1; // Month is 0-indexed
      const day = parseInt(newDate.split('-')[2]);
      
      // Create UK timezone date by treating input as UK local time
      const ukDate = new Date();
      ukDate.setFullYear(year, month, day);
      ukDate.setHours(hour24, parseInt(newMinute), 0, 0);
      
      // Convert to ISO string representing UK time as UTC
      onChange(ukDate.toISOString());
    } catch (error) {
      console.error('Error creating datetime:', error);
    }
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    updateDateTime(newDate, hour, minute, period);
  };

  const handleHourChange = (newHour: string) => {
    setHour(newHour);
    updateDateTime(date, newHour, minute, period);
  };

  const handleMinuteChange = (newMinute: string) => {
    setMinute(newMinute);
    updateDateTime(date, hour, newMinute, period);
  };

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    updateDateTime(date, hour, minute, newPeriod);
  };

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const h = i + 1;
    return (
      <option key={h} value={String(h).padStart(2, '0')}>
        {h}
      </option>
    );
  });

  // Generate minute options (00, 15, 30, 45)
  const minuteOptions = ['00', '15', '30', '45'].map(m => (
    <option key={m} value={m}>
      {m}
    </option>
  ));

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-ink">
          {label}
        </label>
      )}
      
      {/* Date Input */}
      <input
        type="date"
        value={date}
        onChange={(e) => handleDateChange(e.target.value)}
        className={`w-full px-3 py-2 border border-rule rounded-md focus:outline-none focus:ring-2 focus:ring-ochre focus:border-ochre ${className}`}
        required
      />
      
      {/* Time Inputs */}
      <div className="flex space-x-2">
        {/* Hour */}
        <select
          value={hour}
          onChange={(e) => handleHourChange(e.target.value)}
          className={`flex-1 px-3 py-2 border border-rule rounded-md focus:outline-none focus:ring-2 focus:ring-ochre focus:border-ochre ${className}`}
        >
          {hourOptions}
        </select>
        
        <span className="flex items-center text-muted">:</span>
        
        {/* Minute */}
        <select
          value={minute}
          onChange={(e) => handleMinuteChange(e.target.value)}
          className={`flex-1 px-3 py-2 border border-rule rounded-md focus:outline-none focus:ring-2 focus:ring-ochre focus:border-ochre ${className}`}
        >
          {minuteOptions}
        </select>
        
        {/* AM/PM */}
        <select
          value={period}
          onChange={(e) => handlePeriodChange(e.target.value)}
          className={`flex-1 px-3 py-2 border border-rule rounded-md focus:outline-none focus:ring-2 focus:ring-ochre focus:border-ochre ${className}`}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

export default TimeInput12Hour;